"use client";

import React from "react";
import {
  Shield,
  Clock,
  Truck,
  DollarSign,
  CheckCircle2,
  Phone,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface EquipmentWhyChooseUsProps {
  title?: string;
  subtitle?: string;
}

const EquipmentWhyChooseUs: React.FC<EquipmentWhyChooseUsProps> = ({
  title = "Why Choose Our Equipment Rental Services",
  subtitle = "Professional equipment hire with complete peace of mind",
}) => {
  const features = [
    {
      icon: <Shield className="w-5 h-5" />,
      title: "Comprehensive Insurance Coverage",
      highlight: "Complete protection included with every rental",
      points: [
        "Comprehensive damage insurance with $1000 excess",
        "Full liability coverage for your peace of mind",
        "No hidden insurance fees or surprise charges",
        "Clear claims process with dedicated support",
      ],
    },
    {
      icon: <Truck className="w-5 h-5" />,
      title: "Flexible Delivery & Pickup",
      highlight: "Convenient service to suit your schedule",
      points: [
        "Delivery available for $50 to your location",
        "Pickup service for $50 when you're finished",
        "Same-day delivery available subject to availability",
        "Flexible timing to accommodate your project needs",
      ],
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: "Professional Maintenance",
      highlight: "Equipment you can rely on",
      points: [
        "Regular professional maintenance and safety inspections",
        "Only hire out equipment in excellent condition",
        "Detailed service records maintained for all equipment",
        "Emergency support available when you need it",
      ],
    },
    {
      icon: <DollarSign className="w-5 h-5" />,
      title: "Competitive Transparent Pricing",
      highlight: "Fair rates with no hidden costs",
      points: [
        "Competitive daily and weekly rental rates",
        "Significant discounts for longer-term rentals",
        "All-inclusive pricing with clear breakdown",
        "No surprise fees or hidden charges",
      ],
    },
  ];

  return (
    <Card className="overflow-hidden shadow-sm hover:shadow-md transition-shadow mb-8">
      <CardContent className="pt-6 p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold mb-2 text-black">{title}</h2>
          <p className="text-sm text-gray-600">{subtitle}</p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-gray-50 rounded-lg p-4 border border-gray-200"
            >
              {/* Header Section */}
              <div className="flex items-start mb-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 bg-[var(--color-rr-green)]">
                  <div className="text-white">{feature.icon}</div>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-black mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-xs font-medium text-[var(--color-rr-green)]">
                    {feature.highlight}
                  </p>
                </div>
              </div>

              {/* Points List */}
              <ul className="space-y-2">
                {feature.points.map((point, pointIndex) => (
                  <li key={pointIndex} className="flex items-start">
                    <CheckCircle2 className="w-3 h-3 mt-0.5 mr-2 flex-shrink-0 text-[var(--color-rr-green)]" />
                    <span className="text-xs text-gray-700 leading-relaxed">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-6 text-center">
          <div className="bg-gradient-to-r from-[var(--color-rr-green)] to-[var(--color-rr-light-green)] rounded-lg p-6 text-white">
            <h3 className="text-lg font-bold mb-2">
              Ready to Rent Professional Equipment?
            </h3>
            <p className="text-sm text-green-100 mb-4">
              Get started with Australia's trusted equipment rental service
            </p>

            <a
              href="tel:0477746329"
              className="inline-flex items-center justify-center bg-white text-black font-medium py-2 px-6 rounded-lg transition-all duration-300 hover:shadow-lg space-x-2"
            >
              <Phone className="w-4 h-4" />
              <span>Call 04 77 746 329</span>
            </a>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EquipmentWhyChooseUs;
